/**
 * Edge-compatible session tracking for AI agents
 * Uses cookie-based storage to work in Edge Runtime
 */

import type { NextRequest, NextResponse } from 'next/server';
import type { DetectionResult } from '@kya-os/agentshield';

export interface SessionData {
  id: string;
  agent: string;
  confidence: number;
  detectedAt: number;
  expires: number;
}

export interface SessionTrackingConfig {
  enabled: boolean;
  cookieName?: string;
  cookieMaxAge?: number; // in seconds
  encryptionKey?: string;
}

export class EdgeSessionTracker {
  private readonly config: Required<SessionTrackingConfig>;

  constructor(config: SessionTrackingConfig) {
    this.config = {
      enabled: config.enabled,
      cookieName: config.cookieName || '__agentshield_session',
      cookieMaxAge: config.cookieMaxAge || 3600, // 1 hour default
      encryptionKey:
        config.encryptionKey ||
        process.env.AGENTSHIELD_SECRET ||
        'agentshield-default-key',
    };
  }

  /**
   * Track a new AI agent session
   */
  async track(
    _request: NextRequest,
    response: NextResponse,
    result: DetectionResult
  ): Promise<NextResponse> {
    try {
      if (!this.config.enabled || !result.isAgent) {
        return response;
      }

      const sessionData: SessionData = {
        id: crypto.randomUUID(),
        agent: result.detectedAgent?.name || 'unknown',
        confidence: result.confidence,
        detectedAt: Date.now(),
        expires: Date.now() + this.config.cookieMaxAge * 1000,
      };

      // Encrypt session data for security
      const encrypted = await this.encrypt(JSON.stringify(sessionData));

      // Set secure httpOnly cookie
      response.cookies.set(this.config.cookieName, encrypted, {
        httpOnly: true,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'lax',
        maxAge: this.config.cookieMaxAge,
        path: '/',
      });

      return response;
    } catch (error) {
      // Fail gracefully - log error but don't break request
      if (process.env.DEBUG_AGENTSHIELD) {
        console.warn('AgentShield: Failed to track session:', error);
      }
      return response;
    }
  }

  /**
   * Check for existing AI agent session
   */
  async check(request: NextRequest): Promise<SessionData | null> {
    try {
      if (!this.config.enabled) {
        return null;
      }

      const cookie = request.cookies.get(this.config.cookieName);
      if (!cookie?.value) {
        return null;
      }

      // Decrypt and parse session data
      const decrypted = await this.decrypt(cookie.value);
      const session: SessionData = JSON.parse(decrypted);

      // Check if session is expired
      if (session.expires < Date.now()) {
        return null;
      }

      return session;
    } catch (error) {
      // Fail gracefully - invalid or corrupted session
      if (process.env.DEBUG_AGENTSHIELD) {
        console.warn('AgentShield: Failed to check session:', error);
      }
      return null;
    }
  }

  /**
   * Clear an existing session
   */
  clear(response: NextResponse): NextResponse {
    try {
      response.cookies.delete(this.config.cookieName);
    } catch (error) {
      // Fail gracefully
      if (process.env.DEBUG_AGENTSHIELD) {
        console.warn('AgentShield: Failed to clear session:', error);
      }
    }
    return response;
  }

  /**
   * Simple encryption using Web Crypto API (Edge-compatible)
   */
  private async encrypt(data: string): Promise<string> {
    try {
      // For Edge Runtime, use simple base64 encoding with obfuscation
      // In production, consider using Web Crypto API subtle.encrypt()
      const key = this.config.encryptionKey;
      const encoded = new TextEncoder().encode(data);

      // Simple XOR obfuscation
      const obfuscated = new Uint8Array(encoded.length);
      for (let i = 0; i < encoded.length; i++) {
        obfuscated[i] = (encoded[i] || 0) ^ key.charCodeAt(i % key.length);
      }

      // Convert to base64
      return btoa(
        Array.from(obfuscated, byte => String.fromCharCode(byte)).join('')
      );
    } catch (error) {
      // Fallback to simple base64 if encryption fails
      return btoa(data);
    }
  }

  /**
   * Simple decryption (Edge-compatible)
   */
  private async decrypt(data: string): Promise<string> {
    try {
      const key = this.config.encryptionKey;
      const decoded = Uint8Array.from(atob(data), c => c.charCodeAt(0));

      // Reverse XOR obfuscation
      const deobfuscated = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        deobfuscated[i] = (decoded[i] || 0) ^ key.charCodeAt(i % key.length);
      }

      return new TextDecoder().decode(deobfuscated);
    } catch (error) {
      // Fallback to simple base64 if decryption fails
      return atob(data);
    }
  }
}

/**
 * Stateless session checker for non-Next.js environments (Express, etc.)
 * Uses a combination of headers to identify continued sessions
 */
export class StatelessSessionChecker {
  static check(headers: Record<string, string>): SessionData | null {
    try {
      // Check for session headers (set by previous response)
      const agent = headers['x-agentshield-session-agent'];
      const confidence = headers['x-agentshield-session-confidence'];
      const sessionId = headers['x-agentshield-session-id'];

      if (agent && confidence && sessionId) {
        return {
          id: sessionId,
          agent,
          confidence: parseFloat(confidence),
          detectedAt: Date.now(),
          expires: Date.now() + 3600000, // 1 hour
        };
      }

      // Check for cookie-based session (if cookies are parsed)
      const cookieHeader = headers['cookie'];
      if (cookieHeader && cookieHeader.includes('__agentshield_session=')) {
        // Simple cookie parsing
        const match = cookieHeader.match(/__agentshield_session=([^;]+)/);
        if (match && match[1]) {
          try {
            const decoded = atob(match[1]);
            return JSON.parse(decoded);
          } catch {
            // Invalid session data
          }
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  static setHeaders(response: any, session: SessionData): void {
    try {
      // Set session headers for stateless tracking
      if (response.setHeader) {
        response.setHeader('X-AgentShield-Session-Agent', session.agent);
        response.setHeader(
          'X-AgentShield-Session-Confidence',
          session.confidence.toString()
        );
        response.setHeader('X-AgentShield-Session-Id', session.id);
      } else if (response.headers && response.headers.set) {
        response.headers.set('x-agentshield-session-agent', session.agent);
        response.headers.set(
          'x-agentshield-session-confidence',
          session.confidence.toString()
        );
        response.headers.set('x-agentshield-session-id', session.id);
      }
    } catch {
      // Fail gracefully
    }
  }
}
